import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { 
  FaArrowLeft, FaArrowRight, FaBuilding, FaUser, FaSave,
  FaBriefcase, FaCertificate, FaMapMarkerAlt, FaEuroSign,
  FaCalendar, FaCheckCircle
} from 'react-icons/fa';

export default function TalentPublish() {
  const navigate = useNavigate();
  const { API, user } = useContext(AppContext);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [certificationsList, setCertificationsList] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    ad_type: '',
    title: '',
    description: '',
    specialties: [],
    contract_types: [],
    experience_level: '',
    experience_years: '',
    certifications: [],
    other_certifications: '',
    location: '',
    region: '',
    country: 'France',
    mobility: '',
    remote_possible: false,
    availability: '',
    start_date: '',
    duration: '',
    salary_min: '',
    salary_max: '',
    salary_type: 'monthly',
    salary_negotiable: true,
    user_phone: user?.phone || ''
  });

  useEffect(() => {
    if (!user) {
      toast.error('Connectez-vous pour publier une annonce');
      navigate('/login');
      return;
    }
    fetchReferenceData();
  }, [user]);

  const fetchReferenceData = async () => {
    try {
      const [specRes, certRes] = await Promise.all([
        axios.get(`${API}/talents/specialties`),
        axios.get(`${API}/talents/certifications`)
      ]);
      setSpecialties(specRes.data);
      setCertificationsList(certRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.ad_type) {
      toast.error('Choisissez le type d\'annonce');
      setStep(1);
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      setStep(2);
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La description est requise');
      setStep(2);
      return;
    }
    if (formData.specialties.length === 0) {
      toast.error('Sélectionnez au moins une spécialité');
      setStep(3);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null
      };

      const response = await axios.post(`${API}/talents?user_id=${user.id}`, payload);
      toast.success('Annonce soumise ! Elle sera visible après validation par l\'équipe.');
      navigate('/user/mes-annonces-talents');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#0A2540] mb-2">Type d'annonce</h2>
              <p className="text-gray-600">Que souhaitez-vous publier ?</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card 
                className={`cursor-pointer transition-all hover:shadow-lg ${formData.ad_type === 'job_offer' ? 'ring-2 ring-[#635BFF] bg-[#635BFF]/5' : ''}`}
                onClick={() => updateForm('ad_type', 'job_offer')}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-[#635BFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FaBuilding className="text-4xl text-[#635BFF]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A2540] mb-2">Offre d'emploi</h3>
                  <p className="text-gray-600 text-sm">
                    Je suis une entreprise et je cherche du personnel qualifié
                  </p>
                </CardContent>
              </Card>

              <Card 
                className={`cursor-pointer transition-all hover:shadow-lg ${formData.ad_type === 'profile' ? 'ring-2 ring-[#00D4FF] bg-[#00D4FF]/5' : ''}`}
                onClick={() => updateForm('ad_type', 'profile')}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-[#00D4FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FaUser className="text-4xl text-[#00D4FF]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A2540] mb-2">Profil disponible</h3>
                  <p className="text-gray-600 text-sm">
                    Je suis un professionnel et je cherche une mission ou un emploi
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#0A2540] mb-2">
                {formData.ad_type === 'job_offer' ? 'Détails du poste' : 'Votre profil'}
              </h2>
              <p className="text-gray-600">Décrivez votre annonce</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">
                  {formData.ad_type === 'job_offer' ? 'Titre du poste *' : 'Titre de votre profil *'}
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => updateForm('title', e.target.value)}
                  placeholder={formData.ad_type === 'job_offer' ? 'Ex: Opérateur foreuse géotechnique expérimenté' : 'Ex: Hydraulicien expérimenté - 10 ans d\'expérience'}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description détaillée *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder={formData.ad_type === 'job_offer' 
                    ? 'Décrivez le poste, les missions, les conditions de travail...'
                    : 'Décrivez votre parcours, vos compétences, ce que vous recherchez...'}
                  rows={8}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Types de contrat acceptés *</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
                  {[
                    { id: 'cdi', label: 'CDI' },
                    { id: 'cdd', label: 'CDD' },
                    { id: 'interim', label: 'Intérim' },
                    { id: 'freelance', label: 'Freelance' },
                    { id: 'stage', label: 'Stage' }
                  ].map(ct => (
                    <div 
                      key={ct.id}
                      className={`p-3 border rounded-lg cursor-pointer text-center transition-all ${
                        formData.contract_types.includes(ct.id) 
                          ? 'border-[#635BFF] bg-[#635BFF]/10 text-[#635BFF]' 
                          : 'hover:border-gray-300'
                      }`}
                      onClick={() => toggleArrayItem('contract_types', ct.id)}
                    >
                      {ct.label}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Téléphone de contact</Label>
                <Input
                  id="phone"
                  value={formData.user_phone}
                  onChange={(e) => updateForm('user_phone', e.target.value)}
                  placeholder="06 12 34 56 78"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#0A2540] mb-2">Spécialités & Compétences</h2>
              <p className="text-gray-600">Sélectionnez les domaines concernés</p>
            </div>

            <div>
              <Label>Spécialités / Métiers *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {specialties.map(spec => (
                  <div 
                    key={spec.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.specialties.includes(spec.id) 
                        ? 'border-[#635BFF] bg-[#635BFF]/10 text-[#635BFF]' 
                        : 'hover:border-gray-300'
                    }`}
                    onClick={() => toggleArrayItem('specialties', spec.id)}
                  >
                    <span className="text-sm">{spec.name_fr}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Niveau d'expérience {formData.ad_type === 'job_offer' ? 'requis' : ''}</Label>
              <Select value={formData.experience_level} onValueChange={(v) => updateForm('experience_level', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debutant">Débutant (0-2 ans)</SelectItem>
                  <SelectItem value="junior">Junior (2-5 ans)</SelectItem>
                  <SelectItem value="confirme">Confirmé (5-10 ans)</SelectItem>
                  <SelectItem value="senior">Senior (10-15 ans)</SelectItem>
                  <SelectItem value="expert">Expert (15+ ans)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Certifications</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 max-h-60 overflow-y-auto">
                {certificationsList.map(cert => (
                  <div 
                    key={cert.id}
                    className={`p-2 border rounded cursor-pointer text-sm transition-all ${
                      formData.certifications.includes(cert.id) 
                        ? 'border-green-500 bg-green-50 text-green-700' 
                        : 'hover:border-gray-300'
                    }`}
                    onClick={() => toggleArrayItem('certifications', cert.id)}
                  >
                    {formData.certifications.includes(cert.id) && <FaCheckCircle className="inline mr-1" />}
                    {cert.name}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="other_cert">Autres certifications</Label>
              <Input
                id="other_cert"
                value={formData.other_certifications}
                onChange={(e) => updateForm('other_certifications', e.target.value)}
                placeholder="Certifications non listées ci-dessus..."
                className="mt-1"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#0A2540] mb-2">Localisation & Disponibilité</h2>
              <p className="text-gray-600">Où et quand ?</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Ville</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => updateForm('location', e.target.value)}
                  placeholder="Paris, Lyon, Marseille..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="region">Région</Label>
                <Input
                  id="region"
                  value={formData.region}
                  onChange={(e) => updateForm('region', e.target.value)}
                  placeholder="Île-de-France, PACA..."
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Mobilité</Label>
              <Select value={formData.mobility} onValueChange={(v) => updateForm('mobility', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Local uniquement</SelectItem>
                  <SelectItem value="regional">Régional</SelectItem>
                  <SelectItem value="national">National</SelectItem>
                  <SelectItem value="international">International</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Disponibilité</Label>
              <Select value={formData.availability} onValueChange={(v) => updateForm('availability', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immédiate</SelectItem>
                  <SelectItem value="date">À partir d'une date</SelectItem>
                  <SelectItem value="negotiable">À négocier</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.availability === 'date' && (
              <div>
                <Label htmlFor="start_date">Date de début</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => updateForm('start_date', e.target.value)}
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <Label htmlFor="duration">Durée (pour missions temporaires)</Label>
              <Input
                id="duration"
                value={formData.duration}
                onChange={(e) => updateForm('duration', e.target.value)}
                placeholder="Ex: 3 mois, 6 mois, 1 an..."
                className="mt-1"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#0A2540] mb-2">Rémunération</h2>
              <p className="text-gray-600">Optionnel mais recommandé</p>
            </div>

            <div>
              <Label>Type de rémunération</Label>
              <Select value={formData.salary_type} onValueChange={(v) => updateForm('salary_type', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Taux horaire</SelectItem>
                  <SelectItem value="daily">Taux journalier</SelectItem>
                  <SelectItem value="monthly">Salaire mensuel</SelectItem>
                  <SelectItem value="yearly">Salaire annuel</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="salary_min">Minimum (€)</Label>
                <Input
                  id="salary_min"
                  type="number"
                  value={formData.salary_min}
                  onChange={(e) => updateForm('salary_min', e.target.value)}
                  placeholder="Ex: 2500"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="salary_max">Maximum (€)</Label>
                <Input
                  id="salary_max"
                  type="number"
                  value={formData.salary_max}
                  onChange={(e) => updateForm('salary_max', e.target.value)}
                  placeholder="Ex: 3500"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="salary_negotiable"
                checked={formData.salary_negotiable}
                onCheckedChange={(v) => updateForm('salary_negotiable', v)}
              />
              <Label htmlFor="salary_negotiable" className="cursor-pointer">
                Rémunération négociable
              </Label>
            </div>

            {/* Summary */}
            <Card className="bg-gray-50 mt-8">
              <CardHeader>
                <CardTitle className="text-lg">Résumé de votre annonce</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p><strong>Type :</strong> {formData.ad_type === 'job_offer' ? 'Offre d\'emploi' : 'Profil disponible'}</p>
                <p><strong>Titre :</strong> {formData.title || '-'}</p>
                <p><strong>Contrats :</strong> {formData.contract_types.map(ct => ct.toUpperCase()).join(', ') || '-'}</p>
                <p><strong>Spécialités :</strong> {formData.specialties.length} sélectionnée(s)</p>
                <p><strong>Localisation :</strong> {formData.location || '-'}</p>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/talents')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#0A2540] mb-4"
          >
            <FaArrowLeft /> Retour
          </button>
          <h1 className="text-3xl font-bold text-[#0A2540]">Publier une annonce</h1>
          <p className="text-gray-600">Gratuit et sans engagement</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                  s <= step ? 'bg-[#635BFF] text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? <FaCheckCircle /> : s}
              </div>
              {s < 5 && (
                <div className={`w-12 md:w-24 h-1 ${s < step ? 'bg-[#635BFF]' : 'bg-gray-200'}`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <Card>
          <CardContent className="p-8">
            {renderStep()}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setStep(s => Math.max(1, s - 1))}
                disabled={step === 1}
              >
                <FaArrowLeft className="mr-2" /> Précédent
              </Button>

              {step < 5 ? (
                <Button
                  onClick={() => setStep(s => Math.min(5, s + 1))}
                  disabled={step === 1 && !formData.ad_type}
                  className="bg-[#635BFF] hover:bg-[#5246E5]"
                >
                  Suivant <FaArrowRight className="ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Publication...' : <>
                    <FaSave className="mr-2" /> Publier l'annonce
                  </>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
